import { useCallback, useMemo, useState } from 'react';
import { ButtonVariants, Table } from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { buildTableLabels } from '@/config/tableLabels';
import { useTableState, useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { useGetAccountMaterialsQuery, useGetMaterialFormQuery } from '../../api/materialsApi';
import { buildMaterialsColumns } from '../../constants/materialsColumns';
import { MaterialModal } from './MaterialModal';

const getMaterialRowId = (row) => row.id;

export const MaterialsTable = () => {
  const t = useTranslations();
  const table = useTableState();
  const [editTarget, setEditTarget] = useState(null);

  const { data, isFetching } = useGetAccountMaterialsQuery(table.queryParams);
  const { currentData: material } = useGetMaterialFormQuery(editTarget?.id, {
    skip: !editTarget?.id,
  });

  const rows = data?.rows ?? EMPTY_LIST;

  const handleAdd = useCallback(() => setEditTarget({}), []);
  const handleEdit = useCallback((row) => setEditTarget({ id: row.id }), []);
  const handleCloseModal = useCallback(() => setEditTarget(null), []);

  const handleSearchChange = useCallback(
    (value) => {
      table.setSearch(value);
      table.setPage(1);
    },
    [table],
  );

  const columns = useMemo(
    () => buildMaterialsColumns(t, { serialOffset: (table.page - 1) * table.pageSize }),
    [t, table.page, table.pageSize],
  );

  const toolbarActions = useMemo(
    () => [
      {
        id: 'add-material',
        label: t('MATERIALS_ADD'),
        icon: Plus,
        variant: ButtonVariants.primary,
        onClick: handleAdd,
      },
    ],
    [t, handleAdd],
  );

  const rowActions = useMemo(
    () => [
      {
        id: 'edit',
        label: t('COMMON_EDIT'),
        variant: ButtonVariants.tertiary,
        onClick: handleEdit,
      },
    ],
    [t, handleEdit],
  );

  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('MATERIALS_TAB_EMPTY_TITLE'),
      description: t('MATERIALS_TAB_EMPTY_DESCRIPTION'),
      primaryLabel: t('MATERIALS_ADD'),
      primaryIcon: <Plus />,
      onPrimaryClick: handleAdd,
    }),
    [t, handleAdd],
  );

  const noResults = useMemo(
    () => ({
      title: t('MATERIALS_NO_RESULTS_TITLE'),
      description: t('COMMON_NO_RESULTS_DESCRIPTION'),
    }),
    [t],
  );

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);
  const isModalReady = editTarget !== null && (!editTarget.id || Boolean(material));

  return (
    <>
      <Table
        columns={columns}
        data={rows}
        total={data?.totalCount ?? 0}
        loading={isFetching}
        getRowId={getMaterialRowId}
        searchable
        onSearchChange={handleSearchChange}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        toolbarActions={toolbarActions}
        rowActions={rowActions}
        actionsHeader={t('FIELD_ACTION')}
        emptyState={emptyState}
        noResults={noResults}
        labels={tableLabels}
      />

      <MaterialModal open={isModalReady} onClose={handleCloseModal} material={material} />
    </>
  );
};
